<template>
  <v-card class="login-card elevation-24" max-width="500">
    <!-- Logo and Title Section -->
    <div class="login-header text-center">
      <div class="logo-container mb-10">
        <v-row no-gutters align="center" justify="space-between">
          <img
            src="https://topli-obrok.ordera.app/images/food/logo.png"
            alt="Ordera Logo"
            class="login-logo"
          />
          <!-- Language Selector -->
          <div class="language-selector">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  small
                  class="language-btn"
                >
                  <v-icon left small>mdi-earth</v-icon>
                  {{
                    langList.find((i) => i.code === $i18n.locale)
                      ? langList.find((i) => i.code === $i18n.locale).name
                      : $t("choose_language")
                  }}
                  <v-icon right small>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="(item, index) in langList"
                  :key="index"
                  @click="
                    $i18n.locale = item.code;
                    setLang();
                  "
                >
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </v-row>
      </div>
      <!-- <h2 class="login-title mb-0">{{ $t("apply") }}</h2> -->
    </div>

    <!-- Login Form / QR Scanner -->
    <div class="login-form-container">
      <qrcode-stream @decode="onDecode" v-if="showQR" class="qr-scanner"></qrcode-stream>

      <v-form v-model="valid" v-else class="login-form">
        <!-- Email Field -->
        <div class="form-field mb-4">
          <label class="field-label">{{ $t("email_or_user_name") }}</label>
          <text-field
            :value="email"
            @input="$emit('update:email', $event)"
            :rules="[rules.required]"
            hide-details
            placeholder="admin@example.com"
          />
        </div>

        <!-- Password Field -->
        <div class="form-field mb-4">
          <label class="field-label">{{ $t("password") }}</label>
          <text-field
            :value="password"
            @input="$emit('update:password', $event)"
            :rules="[rules.required]"
            :type="showPassword ? 'text' : 'password'"
            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
            @appendIconClick="showPassword = !showPassword"
            hide-details
          />
        </div>

        <!-- Forgot Password Link -->
        <div class="forgot-password mb-4">
          <router-link :to="{ name: 'auth-forgot-password' }" class="forgot-link">
            {{ $t("forgoten_password") }}
          </router-link>
        </div>

        <!-- Restaurant Selector -->
        <div v-if="sites.length" class="form-field mb-4">
          <label class="field-label">{{ $t("select_restaurant") }}</label>
          <autocomplete
            :items="sites"
            item-text="name"
            item-value="id"
            return-object
            @change="$store.commit('SAVE_RESTAURANT', $event)"
            solo
            flat
            background-color="grey lighten-5"
            hide-details
            class="modern-input"
          />
        </div>

        <!-- Error Alert -->
        <v-fade-transition>
          <v-alert v-show="failed" type="error" dense outlined class="error-alert">
            {{ $t("wrong_password") }}
          </v-alert>
        </v-fade-transition>
      </v-form>
    </div>

    <!-- Action Buttons -->
    <div class="action-buttons">
      <!-- Main Login Button -->
      <primary-button
        v-if="!sites.length"
        class="login-btn"
        height="52"
        block
        @click="handleLogin"
        :loading="loading"
        :disabled="!valid || loading"
        :dark="valid && !loading"
        elevation="2"
      >
        <v-icon left>mdi-login</v-icon>
        {{ $t("sign_up") }}
      </primary-button>

      <!-- Restaurant Selection Button -->
      <primary-button
        v-else
        class="login-btn"
        height="52"
        block
        @click="$router.push({ name: routeName.name })"
      >
        <v-icon left>mdi-check</v-icon>
        {{ $t("select") }}
      </primary-button>

      <!-- QR Code Button -->
      <v-btn
        color="primary"
        outlined
        height="52"
        block
        class="qr-btn mt-3"
        @click="toggleQR"
      >
        <v-icon left>{{ showQR ? "mdi-keyboard" : "mdi-qrcode-scan" }}</v-icon>
        {{ showQR ? $t("back_to_form") || "Nazad na formu" : $t("apply_over_qr") }}
      </v-btn>
    </div>

    <!-- Footer -->
    <div class="login-footer">
      <div class="footer-links">
        <a @click.prevent="$emit('show-support')" class="support-link">
          <v-icon small>mdi-help-circle-outline</v-icon>
          {{ $t("support") || "Podrška" }}
        </a>
      </div>
      <div class="footer-logo">
        <span class="powered-by">Powered by</span>
        <strong class="brand-name">Ordera</strong>
      </div>
    </div>
  </v-card>
</template>

<script>
import { QrcodeStream } from "vue-qrcode-reader";
import setLang from "@/mixins/setLang";
import hasValidation from "@/mixins/hasValidation";

export default {
  name: "LoginCard",
  mixins: [setLang, hasValidation],
  components: {
    QrcodeStream,
  },
  props: {
    email: {
      type: String,
      default: null,
    },
    password: {
      type: String,
      default: null,
    },
    sites: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    failed: {
      type: Boolean,
      default: false,
    },
    showQR: {
      type: Boolean,
      default: false,
    },
    routeName: {
      type: [Object, String],
      default: () => ({}),
    },
    langList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showPassword: false,
    };
  },
  methods: {
    handleLogin() {
      this.$emit("login");
    },
    toggleQR() {
      this.$emit("toggle-qr");
    },
    onDecode(decodedString) {
      this.$emit("qr-decode", decodedString);
    },
  },
};
</script>

<style scoped>
.login-card {
  border-radius: 20px !important;
  padding: 40px 32px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafb 100%);
  border: 1px solid rgba(0, 0, 0, 0.06);
  backdrop-filter: blur(10px);
}

.login-header {
  margin-bottom: 16px;
}

.logo-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.login-logo {
  height: 40px;
  width: auto;
  object-fit: contain;
}

.login-title {
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
}

.login-subtitle {
  font-size: 16px;
  color: #6c757d !important;
  margin: 0;
}

.language-selector {
  display: flex;
  justify-content: center;
}

.language-btn {
  border-radius: 25px !important;
  text-transform: none;
  font-size: 14px;
  min-width: 140px;
  background-color: white !important;
  border: 2px solid #fb6d3b !important;
  color: #fb6d3b !important;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(251, 109, 59, 0.15) !important;
}

.language-btn:hover {
  background-color: rgba(251, 109, 59, 0.05) !important;
  box-shadow: 0 4px 12px rgba(251, 109, 59, 0.25) !important;
}

.login-form-container {
  margin-bottom: 32px;
}

.qr-scanner {
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 16px;
}

.form-field {
  margin-bottom: 20px;
}

.field-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.modern-input {
  border-radius: 12px !important;
}

.modern-input >>> .v-input__control .v-input__slot {
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
}

.modern-input >>> .v-input__control .v-input__slot:hover {
  border-color: #d1d5db;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.modern-input >>> .v-input--is-focused .v-input__slot {
  border-color: #fb6d3b !important;
  box-shadow: 0 0 0 3px rgba(251, 109, 59, 0.1) !important;
}

.forgot-password {
  text-align: right;
}

.forgot-link {
  color: #fb6d3b;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.3s ease;
}

.forgot-link:hover {
  color: #e55a2b;
  text-decoration: underline;
}

.error-alert {
  border-radius: 12px !important;
  margin-bottom: 16px;
}

.action-buttons {
  margin-bottom: 24px;
}

.login-btn {
  border-radius: 14px !important;
  font-weight: 600;
  font-size: 16px;
  text-transform: none;
  letter-spacing: 0.5px;
}

.qr-btn {
  border-radius: 14px !important;
  font-weight: 500;
  font-size: 14px;
  text-transform: none;
  border: 2px solid #fb6d3b !important;
  color: #fb6d3b !important;
}

.qr-btn:hover {
  background-color: rgba(251, 109, 59, 0.04) !important;
}

.login-footer {
  border-top: 1px solid #e5e7eb;
  padding-top: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.footer-links {
  display: flex;
  align-items: center;
}

.support-link {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #6b7280;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.3s ease;
}

.support-link:hover {
  color: #fb6d3b;
}

.footer-logo {
  display: flex;
  align-items: center;
  gap: 4px;
}

.powered-by {
  font-size: 12px;
  color: #9ca3af;
}

.brand-name {
  font-size: 14px;
  color: #fb6d3b;
  font-weight: 700;
}

/* Mobile Responsiveness */
@media (max-width: 480px) {
  .login-card {
    padding: 24px 20px;
    border-radius: 16px !important;
    margin: 16px;
  }

  .login-title {
    font-size: 24px;
  }

  .login-subtitle {
    font-size: 14px;
  }

  .login-footer {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
}

/* Animation for form elements */
.form-field {
  animation: slideInUp 0.5s ease-out;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Loading states */
.login-btn.v-btn--loading {
  pointer-events: none;
}

/* Focus states for accessibility */
.modern-input >>> .v-input__control .v-input__slot:focus-within {
  outline: 2px solid #fb6d3b;
  outline-offset: 2px;
}
</style>
